<template>
  <div class="delay-element-wrapper">
    <div class="element-header">
      <span class="element-type-badge">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
        <span>Délai</span>
      </span>
      <div class="element-actions">
        <button class="action-btn" @click.stop="$emit('duplicate')" title="Dupliquer">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
        </button>
        <button class="action-btn delete-btn" @click.stop="$emit('remove')" title="Supprimer">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
        </button>
      </div>
    </div>
    
    <div class="delay-content">
      <div class="delay-info">
        <div class="timer-display">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M5 22h14"></path>
            <path d="M5 2h14"></path>
            <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"></path>
            <path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"></path>
          </svg>
          <span class="timer-value">{{ formattedDelay }}</span>
        </div>
        <p class="delay-description">Le bot attendra {{ formattedDelay }} avant de continuer</p>
      </div>
      
      <div class="delay-control">
        <label class="control-label">Durée d'attente :</label>
        <div class="slider-container">
          <input 
            type="range" 
            class="delay-slider" 
            :value="sliderValue"
            :min="minValue"
            :max="maxValue"
            :step="step"
            :style="sliderStyle"
            @input="onSliderChange"
            @change="onSliderChange"
          />
          <div class="slider-marks">
            <span class="mark">{{ formatValue(minValue) }}</span>
            <span class="mark">{{ formatValue(maxValue) }}</span>
          </div>
        </div>
        <div class="quick-presets">
          <button 
            v-for="preset in presets" 
            :key="preset.value"
            class="preset-btn"
            :class="{ 'active': currentValue === preset.value }"
            @click="setPreset(preset.value)"
          >
            {{ preset.label }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DelayElement',
  props: {
    element: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      // Valeurs du slider (en secondes)
      minValue: 1,   // 1 seconde
      maxValue: 10,   // 10 secondes
      step: 0.1,     // Pas de 0.1 secondes
      presets: [
        { label: '1s', value: 1 },
        { label: '2s', value: 2 },
        { label: '3s', value: 3 },
        { label: '5s', value: 5 },
        { label: '10s', value: 10 }
      ]
    }
  },
  computed: {
    currentValue() {
      const timer = this.element.timmer || '1';
      return parseFloat(timer) || 1; // Par défaut 1 seconde
    },
    
    sliderValue() {
      return Math.max(this.minValue, Math.min(this.maxValue, this.currentValue));
    },
    
    formattedDelay() {
      const timerValue = this.currentValue;
      
      if (isNaN(timerValue) || timerValue === 0) {
        return '1 seconde';
      }
      
      if (timerValue === 1) {
        return '1 seconde';
      } else {
        return `${timerValue} secondes`;
      }
    },
    
    sliderStyle() {
      const percentage = ((this.sliderValue - this.minValue) / (this.maxValue - this.minValue)) * 100;
      return {
        background: `linear-gradient(to right, #143028 0%, #143028 ${percentage}%, #d1d5db ${percentage}%, #d1d5db 100%)`
      };
    }
  },
  methods: {
    onSliderChange(event) {
      const newValue = parseFloat(event.target.value);
      this.updateDelay(newValue);
    },
    
    setPreset(value) {
      this.updateDelay(value);
    },
    
    updateDelay(value) {
      const updatedElement = { 
        ...this.element, 
        timmer: value.toString() 
      };
      this.$emit('update', updatedElement);
    },
    
    formatValue(value) {
      return `${value}s`;
    }
  }
}
</script>

<style scoped>
.delay-element-wrapper {
  background: #ffffff;
  border-radius: 16px;
  overflow: visible;
  box-shadow: 0 10px 25px rgba(20, 48, 40, 0.08);
  border: 1px solid rgba(20, 48, 40, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-bottom: 20px;
}

.delay-element-wrapper:hover {
  box-shadow: 0 15px 35px rgba(20, 48, 40, 0.12);
  border-color: rgba(218, 255, 209, 0.5);
  transform: translateY(-2px);
}

.element-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  background: #143028; /* Peelo Dark Green */
  border-bottom: 1px solid rgba(218, 255, 209, 0.1);
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
}

.element-type-badge {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  font-weight: 700;
  color: #daffd1; /* Peelo Mint */
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.element-type-badge svg {
  color: #daffd1;
}

.element-actions {
  display: flex;
  gap: 10px;
}

.action-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(218, 255, 209, 0.15);
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(218, 255, 209, 0.7);
  transition: all 0.25s ease;
}

.action-btn:hover {
  background: rgba(218, 255, 209, 0.1);
  color: #daffd1;
  border-color: #daffd1;
  transform: scale(1.05);
}

.action-btn.delete-btn:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #f87171;
  border-color: #ef4444;
}

.delay-content {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.delay-info {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: rgba(218, 255, 209, 0.15);
  border-radius: 10px;
  border: 1px solid rgba(218, 255, 209, 0.5);
}

.timer-display {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #143028;
  border-radius: 50px;
  box-shadow: 0 4px 12px rgba(20, 48, 40, 0.15);
  min-width: 120px;
}

.timer-display svg {
  color: #daffd1;
}

.timer-value {
  font-weight: 700;
  color: #daffd1;
  font-size: 1rem;
}

.delay-description {
  margin: 0;
  color: #143028;
  font-weight: 500;
  font-size: 0.95rem;
}

.delay-control {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.control-label {
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
  margin-bottom: 8px;
}

.slider-container {
  position: relative;
  margin-bottom: 12px;
}

.delay-slider {
  width: 100%;
  height: 8px;
  border-radius: 4px;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.delay-slider::-webkit-slider-thumb {
  appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #143028;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(20, 48, 40, 0.3);
  transition: all 0.2s ease;
}

.delay-slider::-webkit-slider-thumb:hover {
  transform: scale(1.2);
  box-shadow: 0 4px 12px rgba(20, 48, 40, 0.4);
}

.delay-slider::-moz-range-thumb {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #143028;
  cursor: pointer;
  border: none;
  box-shadow: 0 2px 8px rgba(20, 48, 40, 0.3);
  transition: all 0.2s ease;
}

.delay-slider::-moz-range-thumb:hover {
  transform: scale(1.2);
  box-shadow: 0 4px 12px rgba(20, 48, 40, 0.4);
}

.slider-marks {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  padding: 0 10px;
}

.mark {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.quick-presets {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.preset-btn {
  padding: 8px 12px;
  border: 1px solid rgba(20, 48, 40, 0.15);
  border-radius: 20px;
  background: white;
  color: #374151;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.preset-btn:hover {
  border-color: #143028;
  color: #143028;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(20, 48, 40, 0.1);
}

.preset-btn.active {
  background: #143028;
  color: #daffd1;
  border-color: #143028;
  box-shadow: 0 4px 12px rgba(20, 48, 40, 0.2);
}

.preset-btn.active:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 15px rgba(20, 48, 40, 0.3);
}
</style>
